import { Close as CloseIcon, Info as InfoIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Paper,
  Radio,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { PatientSummary } from '../../types';

interface PrimaryPatientSelectorProps {
  patients: PatientSummary[];
  primaryPatientId: string;
  onPrimaryChange: (id: string) => void;
  onRemovePatient: (id: string) => void;
  canRemovePatient: boolean;
  patientColors: Map<string, string>;
}

export function PrimaryPatientSelector({
  patients,
  primaryPatientId,
  onPrimaryChange,
  onRemovePatient,
  canRemovePatient,
  patientColors,
}: PrimaryPatientSelectorProps) {
  const theme = useTheme();

  return (
    <Paper sx={{ mb: 3, p: 2.5, border: `1px solid ${theme.palette.divider}` }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        Choose Primary Patient
      </Typography>

      <Alert icon={<InfoIcon fontSize="small" />} severity="info" sx={{ mb: 2 }}>
        The primary patient&apos;s record is preserved. Notes, appointments, forms, and documents
        from all other patients will be copied into the primary&apos;s chart.
      </Alert>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {patients.map((patient, index) => {
          const isPrimary = patient.patient_id === primaryPatientId;
          const color = patientColors.get(patient.patient_id) ?? theme.palette.grey[500];
          const patientName =
            [patient.first_name, patient.last_name].filter(Boolean).join(' ') ||
            patient.patient_id.substring(0, 8);

          return (
            <Box
              key={patient.patient_id}
              onClick={() => onPrimaryChange(patient.patient_id)}
              role="radio"
              aria-checked={isPrimary}
              aria-label={`Select ${patientName} as primary patient`}
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onPrimaryChange(patient.patient_id);
                }
              }}
              sx={{
                position: 'relative',
                flex: '1 1 220px',
                maxWidth: 320,
                p: 2,
                borderRadius: 2,
                border: `2px solid ${isPrimary ? color : theme.palette.divider}`,
                backgroundColor: isPrimary ? alpha(color, 0.04) : 'transparent',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': {
                  borderColor: color,
                  transform: isPrimary ? 'none' : 'translateY(-1px)',
                  boxShadow: isPrimary ? 'none' : theme.shadows[2],
                },
              }}
            >
              {/* Remove button */}
              {canRemovePatient && (
                <Tooltip title="Remove from merge — this patient will not be included">
                  <IconButton
                    size="small"
                    aria-label={`Remove ${patientName} from merge`}
                    onClick={e => {
                      e.stopPropagation();
                      onRemovePatient(patient.patient_id);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      color: theme.palette.text.secondary,
                      '&:hover': {
                        color: theme.palette.error.main,
                        backgroundColor: alpha(theme.palette.error.main, 0.08),
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {/* Radio + name row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: canRemovePatient ? 3 : 0 }}>
                <Radio
                  checked={isPrimary}
                  size="small"
                  onClick={e => e.stopPropagation()}
                  onChange={() => onPrimaryChange(patient.patient_id)}
                  sx={{ p: 0.5, color }}
                  inputProps={{ 'aria-label': `${patientName} primary` }}
                />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {patientName}
                </Typography>
              </Box>

              {/* Patient index */}
              <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mt: 0.25, fontFamily: 'monospace', fontSize: '0.65rem' }}>
                Patient #{index + 1}
              </Typography>

              {/* PRIMARY chip */}
              {isPrimary && (
                <Chip
                  label="PRIMARY"
                  size="small"
                  sx={{
                    mt: 1,
                    ml: 4,
                    fontWeight: 700,
                    fontSize: '0.675rem',
                    height: 22,
                    backgroundColor: color,
                    color: '#fff',
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}
